'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useUserContext } from '@/lib/contexts/UserContext'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useQuery } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { Button, buttonVariants } from '@/components/ui/button'
import Link from 'next/link'
import styles from './asset-popup.module.scss'
import Image from 'next/image'
import { cn, formatBytes } from '@/lib/utils'
import Asset360Viewer from '@/components/asset-360-viewer/asset-360-viewer'
import { TbView360Number } from 'react-icons/tb'
import { ReactPhotoSphereViewer } from 'react-photo-sphere-viewer'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { ChevronDownIcon, StarFilledIcon } from '@radix-ui/react-icons'
import { ArrowLeftIcon, ArrowRightIcon, LoaderCircleIcon } from 'lucide-react'
import { useHotkeys } from 'react-hotkeys-hook'
import useAsset, { DownloadOption, FormatOption, ResolutionOption } from '@/lib/hooks/useAsset'
import { sortResolutionOptions } from '@/lib/assets/resolution-score'
import { Asset } from '@/lib/assets/types'
import { MusicBar } from '@/components/ui/music-bar'
import { forDetails } from '@/lib/assets/repositories/asset-repository-factory'
import { useUserRealtimeContext } from '@/lib/contexts/UserRealtimeContext'
import { PortalDownloadsToggle } from '@/components/portal-downloads-toggle'
import { useAssetPopupStore } from '@/lib/store/asset-popup-store'
import { useAppStore } from '@/lib/store/store'

// TODO: Convert TailwindCSS to SCSS
export type AssetPopupProps = {
  assetId: string | number | null
  mainCategoryId: string
  categoryId?: string
  subCategoryId?: string
  contentId?: string
  hasPrev?: boolean
  hasNext?: boolean
  onOpenChange?: (open: boolean) => void
  onNext?: () => void
  onPrev?: () => void
  disableNavigation?: boolean
}

const formatSecondsAsTimestamp = (rawSeconds?: string | number) => {
  if (rawSeconds === undefined || rawSeconds === null) {
    return null
  }

  const parsed = typeof rawSeconds === 'number' ? rawSeconds : Number.parseFloat(rawSeconds)

  if (!Number.isFinite(parsed)) {
    return null
  }

  const normalized = Math.round(parsed * 100) / 100
  let wholeSeconds = Math.floor(normalized)
  let fractional = Math.round((normalized - wholeSeconds) * 100)

  if (fractional === 100) {
    wholeSeconds += 1
    fractional = 0
  }

  const secondsPart = wholeSeconds.toString().padStart(2, '0')
  const fractionalPart = fractional.toString().padStart(2, '0')

  return `${secondsPart}:${fractionalPart}`
}

const AssetPopup = ({
  assetId,
  mainCategoryId,
  categoryId,
  subCategoryId,
  contentId,
  hasPrev,
  hasNext,
  onOpenChange = () => {},
  onNext = () => {},
  onPrev = () => {},
  disableNavigation,
}: AssetPopupProps) => {
  const [selectedResolution, setSelectedResolution] = useState<ResolutionOption | null>(null)
  const [selectedFormat, setSelectedFormat] = useState<FormatOption | null>(null)
  const [resolutionCardOpen, setResolutionCardOpen] = useState<boolean | undefined>(undefined)
  const [formatCardOpen, setFormatCardOpen] = useState<boolean | undefined>(undefined)
  const [isMetaKeyPressed, setIsMetaKeyPressed] = useState<boolean>(false)
  const [isBlenderAddon, setIsBlenderAddon] = useState<boolean>(false)
  const { user, userToken } = useUserContext()
  const setPopupOpen = useAssetPopupStore((state) => state.setIsOpen)
  const downloadFormatPreferences = useAppStore((state) => state.downloadFormatPreferences)
  const setDownloadFormatPreference = useAppStore((state) => state.setDownloadFormatPreference)

  const { data: asset, isLoading } = useQuery<Asset | null>({
    queryKey: ['asset', assetId],
    queryFn: async () => {
      if (!assetId) {
        return null
      }

      const repository = forDetails()

      return await repository.getAssetByIdOrSlug({
        assetIdOrSlug: `${assetId}`,
        mainCategoryIdOrSlug: mainCategoryId,
        categoryIdOrSlug: categoryId,
        subCategoryIdOrSlug: subCategoryId,
        contentIdOrSlug: contentId,
      })
    },
    enabled: !!assetId,
  })

  const {
    resolutions,
    formats,
    videoPreview,
    imagePreview,
    asset360Preview,
    panoramaPreview,
    audioPreview,
    assetDetailUrl,
    downloadOptions,
    download,
    isDownloading,
  } = useAsset({
    asset: asset ?? undefined,
    userToken,
  })

  // Determine if portal download is possible
  const { state: userStatus, download_method: downloadMethod } = useUserRealtimeContext()
  const isPortalDownload = userStatus === 'online' && downloadMethod === 'portal'

  const secondsValue = useMemo(() => {
    if (!asset?.properties || typeof asset.properties !== 'object') {
      return undefined
    }

    const rawSeconds = (asset.properties as Record<string, unknown>).seconds

    if (typeof rawSeconds === 'number') {
      return rawSeconds
    }

    if (typeof rawSeconds === 'string') {
      const parsed = Number.parseFloat(rawSeconds)
      return Number.isFinite(parsed) ? parsed : undefined
    }

    return undefined
  }, [asset?.properties])

  const durationDisplay = useMemo<{ timestamp: string | null; rounded: string | null }>(() => {
    if (typeof secondsValue !== 'number') {
      return { timestamp: null, rounded: null }
    }

    const normalized = Math.round(Math.max(secondsValue, 0) * 100) / 100

    return {
      timestamp: formatSecondsAsTimestamp(normalized),
      rounded: normalized.toFixed(2),
    }
  }, [secondsValue])

  const resolutionsForSelectedFormat = useMemo(() => {
    if (!selectedFormat) {
      return resolutions
    }

    const uniqueResolutions = new Map<string, ResolutionOption>()

    downloadOptions.forEach((option) => {
      if (option.format?.value === selectedFormat.value && option.resolution) {
        const value = option.resolution.value
        if (!uniqueResolutions.has(value)) {
          uniqueResolutions.set(value, option.resolution)
        }
      }
    })

    if (uniqueResolutions.size === 0) {
      return resolutions
    }

    return sortResolutionOptions(Array.from(uniqueResolutions.values()))
  }, [downloadOptions, resolutions, selectedFormat])

  // Use left and right arrow keys to navigate between assets
  useHotkeys('ArrowLeft', () => hasPrev && onPrev())
  useHotkeys('ArrowRight', () => hasNext && onNext())
  useHotkeys('meta', () => setIsMetaKeyPressed(true), { keydown: true, keyup: false })
  useHotkeys('meta', () => setIsMetaKeyPressed(false), { keydown: false, keyup: true })

  useEffect(() => {
    if (!!videoPreview && user && user.settings && user.settings.footagecrateBlenderAddon) {
      setIsBlenderAddon(true)
    } else {
      setIsBlenderAddon(false)
    }
  }, [user, videoPreview])

  // Based on selectFormat and selectResolution, get the selected option
  const selectedOption: DownloadOption | null = useMemo(() => {
    if (!downloadOptions || !selectedFormat) {
      return null
    }
    return (
      downloadOptions.find(
        (option) =>
          option.format?.value === selectedFormat.value &&
          (!selectedResolution || option.resolution?.value === selectedResolution.value)
      ) ?? null
    )
  }, [downloadOptions, selectedFormat, selectedResolution])

  const selectedLevel = useMemo(() => {
    if (selectedOption?.level !== undefined && selectedOption?.level !== null) {
      const parsed = typeof selectedOption.level === 'number' ? selectedOption.level : Number(selectedOption.level)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }

    return asset?.level ?? 0
  }, [asset?.level, selectedOption?.level])

  const isSelectedPremium = useMemo(() => Number(selectedLevel) > 0, [selectedLevel])

  const proResolutions = useMemo(() => {
    if (!asset || !downloadOptions?.length) return new Set<string>()

    const set = new Set<string>()

    if (asset.level === 0) {
      downloadOptions.forEach((option) => {
        if (option.resolution?.value && Number(option.level) > 0) {
          set.add(option.resolution.value)
        }
      })
    }

    return set
  }, [asset, downloadOptions])

  const resolutionIsProOnly = useCallback(
    (resolution?: ResolutionOption | null) => {
      if (!resolution) return false
      return proResolutions.has(resolution.value)
    },
    [proResolutions]
  )

  // Ensure the selected resolution is resetted when asset changes
  useEffect(() => {
    console.debug('Asset changed, resetting selected resolution and format')
    setSelectedResolution(null)
    setSelectedFormat(null)
  }, [assetId])

  // Sync popup open state with store
  useEffect(() => {
    setPopupOpen(!!assetId)
    return () => setPopupOpen(false)
  }, [assetId, setPopupOpen])

  // Select a first resolution option by default
  // Select a format option by default. Check per-vertical session preference first, then user settings.
  useEffect(() => {
    if (formats.length === 0 || selectedFormat) {
      return
    }

    const verticalPref = asset?.vertical ? downloadFormatPreferences[asset.vertical] : null

    if (verticalPref?.format) {
      const storedFormat = formats.find((f) => f.value === verticalPref.format)
      if (storedFormat) {
        setSelectedFormat(storedFormat)
        return
      }
    }

    if (user?.settings?.videoFormat === 'seq') {
      user.settings.videoFormat = 'png sequence'
    }
    const preferredFormat = !!user?.settings?.videoFormat
      ? formats.find((format) => format.value.toLowerCase() === `${user?.settings?.videoFormat}`.toLowerCase())
      : null

    if (preferredFormat) {
      setSelectedFormat(preferredFormat)
      return
    }

    setSelectedFormat(formats[0])
  }, [formats, selectedFormat, user?.settings, asset?.vertical, downloadFormatPreferences])

  // Ensure selected resolution aligns with the chosen format
  useEffect(() => {
    if (!selectedFormat) {
      setSelectedResolution(null)
      return
    }

    if (resolutionsForSelectedFormat.length === 0) {
      setSelectedResolution(null)
      return
    }

    setSelectedResolution((current) => {
      if (current && resolutionsForSelectedFormat.some((resolution) => resolution.value === current.value)) {
        return current
      }

      const verticalPref = asset?.vertical ? downloadFormatPreferences[asset.vertical] : null
      if (verticalPref?.resolution) {
        const storedResolution = resolutionsForSelectedFormat.find((r) => r.value === verticalPref.resolution)
        if (storedResolution) return storedResolution
      }

      return resolutionsForSelectedFormat[0]
    })
  }, [resolutionsForSelectedFormat, selectedFormat, asset?.vertical, downloadFormatPreferences])

  // Debug available resolutions
  useEffect(() => {
    console.debug('Available resolutions:', resolutions)
  }, [resolutions])

  // Debug resolutions and formats
  useEffect(() => {
    if (selectedFormat) {
      console.debug(`Available resolutions for ${selectedFormat.label} format:`, resolutionsForSelectedFormat)
    }
  }, [resolutionsForSelectedFormat, selectedFormat])

  // Debug all download options
  useEffect(() => {
    console.debug('All download options:', downloadOptions)
  }, [downloadOptions])

  // Debug selected resolution and format
  useEffect(() => {
    console.debug('Selected resolution:', selectedResolution)
    console.debug('Selected format:', selectedFormat)
    console.debug('Selected option:', selectedOption)
  }, [selectedResolution, selectedFormat, selectedOption])

  return (
    <>
      <Dialog
        open={!!assetId}
        onOpenChange={(value) => {
          if (!value) {
            setSelectedResolution(null)
            setSelectedFormat(null)
          }
          onOpenChange(value)
        }}
      >
        <DialogContent data-slot="asset-popup-dialog-content" className={cn(styles.popup, 'tw-relative tw-@container')}>
          <DialogHeader>
            <DialogTitle className={cn(styles.title, 'tw-flex tw-items-center tw-space-x-1')}>
              <>
                {isLoading && <Skeleton className={styles.titleSkeleton} />}
                {!isLoading && !!asset && (
                  <>
                    {isSelectedPremium && (
                      <span title="Premium asset" className="tw-text-[var(--Secondary-200)]">
                        <StarFilledIcon className={styles.starIcon} />
                      </span>
                    )}
                    <Link href={assetDetailUrl ?? '#'}>
                      <span className="hover:tw-underline">{asset.name.replace('(4K)', '').replace('4K', '')}</span>
                      {asset.name.includes('4K') && <span className={styles.small}>{' (4K)'}</span>}
                    </Link>
                  </>
                )}
              </>
            </DialogTitle>
          </DialogHeader>

          <div className="tw-flex tw-flex-col tw-space-y-4">
            {isLoading && (
              <>
                <Skeleton className={styles.preview} />
                <Skeleton className="tw-h-10 tw-w-full" />
              </>
            )}
            {!isLoading && !!asset && (
              <>
                {/*Use the key prop to force re-render when the value changes*/}
                <div
                  className="tw-relative tw-flex tw-min-h-[360px] tw-w-full tw-flex-col tw-justify-center tw-overflow-hidden tw-rounded-lg tw-bg-[var(--Primary)] tw-duration-300 tw-animate-in tw-fade-in"
                  key={`asset-popup-preview-${assetId}`}
                >
                  {/* Asset has a video preview */}
                  {!!videoPreview && (
                    <video
                      data-poster={imagePreview ?? undefined}
                      preload="auto"
                      autoPlay={true}
                      muted
                      loop
                      playsInline={true}
                      poster=""
                      className={cn(styles.preview, 'tw-absolute tw-left-0 tw-top-0 tw-mx-auto tw-h-full tw-w-full')}
                    >
                      <source src={videoPreview} type="video/mp4"></source>
                    </video>
                  )}

                  {/* Asset has an image preview */}
                  {!videoPreview && !!imagePreview && (
                    <Image
                      src={imagePreview}
                      alt={'title' in asset ? asset.name : ''}
                      width={640}
                      height={0}
                      style={{
                        width: '100%',
                        maxWidth: '640px',
                        height: '100%',
                        objectFit: 'contain',
                      }}
                      className={cn(
                        styles.imagePreview,
                        'tw-absolute tw-left-0 tw-top-0 tw-mx-auto tw-h-full tw-w-full'
                      )}
                    />
                  )}

                  {/*Asset has a 360 preview*/}
                  {!!asset360Preview && (
                    <>
                      <Asset360Viewer
                        className="tw-absolute tw-left-0 tw-top-0 tw-mx-auto tw-h-full tw-w-full"
                        friction={30}
                        sprite={asset360Preview}
                        frames={16} // Number of frames in the sprite sheet
                        frameWidth={640} // Width of each frame
                        frameHeight={360} // Height of each frame
                      />
                      <TbView360Number
                        title="360º preview"
                        className="tw-pointer-events-none tw-absolute tw-bottom-2 tw-left-2 tw-h-6 tw-w-6 tw-text-[var(--Primary-100)] tw-opacity-50"
                      />
                    </>
                  )}

                  {/*Panorama preview*/}
                  {!!panoramaPreview && (
                    <>
                      <ReactPhotoSphereViewer
                        src={panoramaPreview}
                        height={'100%'}
                        width={'100%'}
                        navbar={false}
                        defaultZoomLvl={0}
                      ></ReactPhotoSphereViewer>
                      <TbView360Number
                        title="360º preview"
                        className="tw-pointer-events-none tw-absolute tw-bottom-2 tw-left-2 tw-h-6 tw-w-6 tw-text-[var(--Primary-100)] tw-opacity-50"
                      />
                    </>
                  )}

                  {/*Audio preview*/}
                  {audioPreview && <MusicBar asset={asset} hideTitle hideTools />}
                </div>

                {/*Download options*/}
                <div
                  key={`asset-popup-download-options-${assetId}`}
                  className="tw-flex tw-w-full tw-flex-col tw-items-start tw-justify-start tw-space-y-2 @xl:tw-flex-row @xl:tw-items-center @xl:tw-justify-between @xl:tw-space-x-2 @xl:tw-space-y-0"
                >
                  {/* Dimensions */}
                  <div
                    className={`tw-flex tw-items-end tw-space-x-1 @xl:tw-flex-1 @xl:tw-flex-col @xl:tw-items-start @xl:tw-space-x-0`}
                  >
                    {'dimensions' in asset.properties && (
                      <div className="subheading_4">{asset.properties.dimensions}</div>
                    )}

                    {'seconds' in asset.properties &&
                      'fps' in asset.properties &&
                      !!asset.properties.seconds &&
                      asset.properties.fps && (
                        <div className={['body_4', styles.measures].join(' ')}>
                          <span>{durationDisplay.timestamp ?? durationDisplay.rounded ?? '--:--'}</span>
                          <span>
                            {asset.properties.fps}
                            <span className={styles.small}>{' FPS'}</span>
                          </span>
                        </div>
                      )}
                  </div>

                  {/* Download options */}
                  <div className="tw-flex tw-items-center tw-space-x-2">
                    {/*Formats hover card*/}
                    {formats.length > 0 && (
                      <HoverCard
                        openDelay={0}
                        closeDelay={100}
                        open={formatCardOpen}
                        onOpenChange={(open) => {
                          setFormatCardOpen(open ?? undefined)
                        }}
                      >
                        <HoverCardTrigger asChild>
                          <div
                            className="tw-flex tw-cursor-pointer tw-items-center tw-p-1"
                            style={{
                              padding: '4px 8px',
                              border: '1.647px solid #424351',
                              borderRadius: '7px',
                              width: 'fit-content',
                              minWidth: '140px',
                            }}
                          >
                            <ChevronDownIcon
                              className="tw-mr-1 tw-h-4 tw-w-4"
                              color="#F36C17"
                              style={{
                                width: '28px',
                                height: '28px',
                                transition: 'transform 0.25s ease-in-out',
                                transform: formatCardOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              }}
                            />
                            {selectedFormat?.label}
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="bottom"
                          sideOffset={0}
                          align="start"
                          className="!tw-rounded tw-border-0 !tw-bg-[var(--Primary)] !tw-p-2"
                        >
                          {formats.map((format: FormatOption) => (
                            <button
                              key={`format-item-${format.value}`}
                              type="button"
                              className={cn(
                                'tw-relative tw-flex tw-w-full tw-cursor-default tw-p-2 tw-outline-none tw-transition-all tw-duration-150 tw-ease-in-out hover:tw-font-bold',
                                {
                                  'tw-font-bold': selectedFormat?.value === format.value,
                                }
                              )}
                              onClick={() => {
                                console.log('format', format)
                                setSelectedFormat(format)
                                setFormatCardOpen(false)
                                if (asset?.vertical) {
                                  setDownloadFormatPreference(asset.vertical, format.value, null)
                                }
                              }}
                            >
                              {format.label}
                            </button>
                          ))}
                        </HoverCardContent>
                      </HoverCard>
                    )}
                    {/*Resolution hover card*/}
                    {resolutionsForSelectedFormat.length > 0 && (
                      <HoverCard
                        openDelay={0}
                        closeDelay={100}
                        open={resolutionCardOpen}
                        onOpenChange={(open) => {
                          setResolutionCardOpen(open ?? undefined)
                        }}
                      >
                        <HoverCardTrigger asChild>
                          <div
                            className="tw-flex tw-cursor-pointer tw-items-center tw-p-1"
                            style={{
                              padding: '4px 8px',
                              border: '1.647px solid #424351',
                              borderRadius: '7px',
                              width: 'fit-content',
                              minWidth: '140px',
                            }}
                          >
                            <ChevronDownIcon
                              className="tw-mr-1 tw-h-4 tw-w-4"
                              color="#F36C17"
                              style={{
                                width: '28px',
                                height: '28px',
                                transition: 'transform 0.25s ease-in-out',
                                transform: resolutionCardOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              }}
                            />
                            <div className="tw-flex tw-items-center tw-space-x-2">
                              <span>{selectedResolution?.label}</span>
                              {resolutionIsProOnly(selectedResolution) && (
                                <span className="tw-flex tw-items-center tw-space-x-1 tw-rounded-full tw-bg-[#5a5a64] tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-leading-none tw-text-white">
                                  <StarFilledIcon className="tw-h-3 tw-w-3 tw-text-white" />
                                  <span>Pro only</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="bottom"
                          sideOffset={0}
                          align="start"
                          className="!tw-rounded tw-border-0 !tw-bg-[var(--Primary)] !tw-p-2"
                        >
                          {React.Children.toArray(
                            resolutionsForSelectedFormat.map((resolution: ResolutionOption) => (
                              <button
                                type="button"
                                className={cn(
                                  'tw-relative tw-flex tw-w-full tw-cursor-default tw-items-center tw-justify-between tw-p-2 tw-outline-none tw-transition-all tw-duration-150 tw-ease-in-out hover:tw-font-bold',
                                  {
                                    'tw-font-bold': selectedResolution?.value === resolution.value,
                                  }
                                )}
                                key={`resolution-item-${resolution.value}`}
                                onClick={() => {
                                  setSelectedResolution(resolution)
                                  setResolutionCardOpen(false)
                                  if (asset?.vertical && selectedFormat) {
                                    setDownloadFormatPreference(asset.vertical, selectedFormat.value, resolution.value)
                                  }
                                }}
                              >
                                <span>{resolution.label}</span>
                                {resolutionIsProOnly(resolution) && (
                                  <span className="tw-flex tw-items-center tw-space-x-1 tw-rounded-full tw-bg-[#5a5a64] tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-leading-none tw-text-white">
                                    <StarFilledIcon className="tw-h-3 tw-w-3 tw-text-white" />
                                    <span>Pro only</span>
                                  </span>
                                )}
                              </button>
                            ))
                          )}
                        </HoverCardContent>
                      </HoverCard>
                    )}
                  </div>
                  {isBlenderAddon && (
                    <Button
                      variant="stroke"
                      className={cn(
                        'tw-ml-1',
                        'btn text_button btn__stroke btn__icon',
                        'tw-w-full @xl:!tw-mt-0 @xl:tw-w-auto'
                      )}
                      onClick={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        if (!selectedOption) {
                          console.warn('No download option selected')
                          return
                        }

                        download(selectedOption.file, {
                          platform: 'blender',
                        })
                      }}
                    >
                      <img
                        src="https://public.productioncrate.com/img/blender-icon_color.svg"
                        style={{ width: 'auto', height: '19px' }}
                        alt="Blender logo"
                      ></img>
                    </Button>
                  )}
                  {/*Download button*/}
                  <a
                    href={selectedOption?.url ?? '#'}
                    rel="noopener noreferrer"
                    className={cn('tw-ml-1', buttonVariants(), 'tw-w-full @xl:!tw-mt-0 @xl:tw-w-auto', {
                      '!tw-pointer-events-none tw-opacity-50': !selectedOption || isDownloading,
                    })}
                    onClick={(event) => {
                      event.preventDefault()

                      if (isDownloading) {
                        console.warn('Download already in progress')
                        return false
                      }

                      if (!selectedOption) {
                        console.warn('No download option selected')
                        return false
                      }

                      // Download
                      download(selectedOption.file, {
                        platform: isPortalDownload ? 'portal' : 'web',
                      })
                    }}
                  >
                    <div className="tw-relative">
                      <span
                        className={cn('tw-visible', {
                          'tw-invisible': isDownloading,
                        })}
                      >
                        Download
                        {!!selectedOption && selectedOption.size && (
                          <span className="tw-ml-1 tw-inline-block tw-w-[64px] tw-text-xs">
                            {formatBytes(selectedOption.size)}
                          </span>
                        )}
                      </span>
                      <div
                        className={cn(
                          'tw-absolute tw-left-0 tw-top-0 tw-flex tw-w-full tw-items-center tw-justify-center',
                          {
                            'tw-hidden': !isDownloading,
                            'tw-flex': isDownloading,
                          }
                        )}
                      >
                        <LoaderCircleIcon className="tw-size-4 tw-animate-spin" />
                      </div>
                    </div>
                  </a>
                </div>

                {/*{false && (*/}
                {/*  <div className={styles.footer}>*/}
                {/*    {keywords.length > 0 && <div className={`body_5`}>Tags</div>}*/}
                {/*    {keywords*/}
                {/*      .filter((e) => !!e)*/}
                {/*      .map((tag: any, index: any) => (*/}
                {/*        <span key={index} className={`${styles.chip} body_5 tw-mr-1`}>*/}
                {/*          {tag}*/}
                {/*        </span>*/}
                {/*      ))}*/}
                {/*  </div>*/}
                {/*)}*/}

                {/*TODO: Related assets*/}
              </>
            )}
          </div>
          {!!hasPrev && (
            <Button
              className="tw-absolute tw--left-12 tw-top-1/2 !tw-hidden tw-translate-y--1/2 md:!tw-inline-flex"
              variant={'ghost'}
              size={'icon'}
              shape={'rounded'}
              onClick={onPrev}
              title={'Previous asset'}
              disabled={disableNavigation}
            >
              <ArrowLeftIcon className="tw-h-6 tw-w-6" />
            </Button>
          )}
          {!!hasNext && (
            <Button
              className="tw-absolute tw--right-12 tw-top-1/2 !tw-hidden tw-translate-y--1/2 md:!tw-inline-flex"
              variant={'ghost'}
              size={'icon'}
              shape={'rounded'}
              onClick={onNext}
              title={'Next asset'}
              disabled={disableNavigation}
            >
              <ArrowRightIcon className="tw-h-6 tw-w-6" />
            </Button>
          )}
        </DialogContent>
      </Dialog>
      <PortalToggleOverlay isOpen={!!assetId} />
    </>
  )
}

const PortalToggleOverlay = ({ isOpen }: { isOpen: boolean }) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  if (!isOpen || !mounted) return null

  return createPortal(
    <div
      className={styles.portalToggleWrapper}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <PortalDownloadsToggle />
    </div>,
    document.body
  )
}

export { AssetPopup }
